import { useQuery } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

import { copyToClipboard } from '@nesso/ui/lib/clipboard';
import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';

import { sessionByIdOptions } from '@/entities/session';
import { formatNote, useIsGenerating } from '@/entities/session-note';
import { isNotEmpty } from '@/shared/lib/object-utils';

type Props = {
    sessionId: string;
    className?: string;
};

export function CopySessionNote({ sessionId, className }: Props) {
    const { data: session } = useQuery(sessionByIdOptions(sessionId));
    const isGenerating = useIsGenerating(sessionId);
    const hasContent = isNotEmpty(session?.currentNote?.content);

    const copy = async () => {
        const text = formatNote(session);

        if (!text) {
            toast.message("There's no note to copy yet");
            return;
        }

        const copied = await copyToClipboard(text);
        toast.message(copied ? 'Note copied' : 'Could not copy the note — please try again');
    };

    return (
        <Button
            variant="outline"
            onClick={copy}
            className={cn('h-9 w-auto px-4 flex', className)}
            disabled={!hasContent || isGenerating}
        >
            <Copy className="w-4 h-4 mr-1" />
            <span>Copy Note</span>
        </Button>
    );
}
